import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Handle, Position } from "reactflow";
const HANDLE_DOT = "!h-4 !w-4 !rounded-full !border-2 !border-ink-900 !bg-accent-500 hover:!bg-accent-400";
// Bus handles render as a wider square in a different colour so it's
// obvious at a glance that the connection carries a multiplexed payload
// (a dict of key→value pairs) rather than a single value.
const HANDLE_BUS = "!h-4 !w-5 !rounded-sm !border-2 !border-ink-900 !bg-fuchsia-400 hover:!bg-fuchsia-300";
const TONES = {
    "assumption.input": {
        ringColor: "border-emerald-500/30 ring-1 ring-emerald-500/30",
        surface: "bg-emerald-500/10",
        headerBg: "bg-emerald-500/15",
        divider: "border-emerald-500/20",
        chipClass: "bg-emerald-500/20 text-emerald-200",
        chipLabel: "Input",
        selectedRing: "ring-2 ring-emerald-300",
    },
    spreadsheet: {
        ringColor: "border-accent-500/30 ring-1 ring-accent-500/30",
        surface: "bg-accent-500/10",
        headerBg: "bg-accent-500/15",
        divider: "border-accent-500/20",
        chipClass: "bg-accent-500/20 text-accent-200",
        chipLabel: "Compute",
        selectedRing: "ring-2 ring-accent-300",
    },
    output: {
        ringColor: "border-amber-500/30 ring-1 ring-amber-500/30",
        surface: "bg-amber-500/10",
        headerBg: "bg-amber-500/15",
        divider: "border-amber-500/20",
        chipClass: "bg-amber-500/20 text-amber-200",
        chipLabel: "Output",
        selectedRing: "ring-2 ring-amber-300",
    },
    "ai.agent": {
        ringColor: "border-fuchsia-500/30 ring-1 ring-fuchsia-500/30",
        surface: "bg-fuchsia-500/10",
        headerBg: "bg-fuchsia-500/15",
        divider: "border-fuchsia-500/20",
        chipClass: "bg-fuchsia-500/20 text-fuchsia-200",
        chipLabel: "AI Agent",
        selectedRing: "ring-2 ring-fuchsia-300",
    },
    "timeline_sheet": {
        ringColor: "border-cyan-500/30 ring-1 ring-cyan-500/30",
        surface: "bg-cyan-500/10",
        headerBg: "bg-cyan-500/15",
        divider: "border-cyan-500/20",
        chipClass: "bg-cyan-500/20 text-cyan-200",
        chipLabel: "Timeline",
        selectedRing: "ring-2 ring-cyan-300",
    },
    derivatives: {
        ringColor: "border-rose-500/30 ring-1 ring-rose-500/30",
        surface: "bg-rose-500/10",
        headerBg: "bg-rose-500/15",
        divider: "border-rose-500/20",
        chipClass: "bg-rose-500/20 text-rose-200",
        chipLabel: "Derivatives",
        selectedRing: "ring-2 ring-rose-300",
    },
    scenario_chooser: {
        ringColor: "border-violet-500/30 ring-1 ring-violet-500/30",
        surface: "bg-violet-500/10",
        headerBg: "bg-violet-500/15",
        divider: "border-violet-500/20",
        chipClass: "bg-violet-500/20 text-violet-200",
        chipLabel: "Control",
        selectedRing: "ring-2 ring-violet-300",
    },
    expression: {
        ringColor: "border-sky-500/30 ring-1 ring-sky-500/30",
        surface: "bg-sky-500/10",
        headerBg: "bg-sky-500/15",
        divider: "border-sky-500/20",
        chipClass: "bg-sky-500/20 text-sky-200",
        chipLabel: "Expression",
        selectedRing: "ring-2 ring-sky-300",
    },
    confidence_gate: {
        ringColor: "border-yellow-500/30 ring-1 ring-yellow-500/30",
        surface: "bg-yellow-500/10",
        headerBg: "bg-yellow-500/15",
        divider: "border-yellow-500/20",
        chipClass: "bg-yellow-500/20 text-yellow-200",
        chipLabel: "Gate",
        selectedRing: "ring-2 ring-yellow-300",
    },
};
// --- Node components ----------------------------------------------------------
export function AssumptionInputNode({ data, selected }) {
    const tone = TONES["assumption.input"];
    const key = data.config?.key ?? "";
    const source = (data.config?.source || "assumption");
    const sourceLabel = source === "parameter"
        ? "Instrument parameter"
        : source === "context"
            ? "Valuation context"
            : "Approach assumption";
    return (_jsxs(Frame, { tone: tone, selected: !!selected, kind: "assumption.input", children: [_jsx(Header, { tone: tone, title: data.label || "Input" }), _jsxs(Body, { children: [_jsx(FieldLabel, { children: sourceLabel }), _jsx("div", { className: "font-mono text-sm text-ink-100", children: key || "(unset)" })] }), _jsx(PortGrid, { tone: tone, inputs: [], outputs: [{ name: "value" }] })] }));
}
export function OutputNode({ data, selected }) {
    const tone = TONES.output;
    const currency = (data.config?.currency || "").toUpperCase();
    const methodology = data.config?.methodology ?? "";
    return (_jsxs(Frame, { tone: tone, selected: !!selected, kind: "output", children: [_jsx(Header, { tone: tone, title: data.label || "Instrument value" }), _jsx(Body, { children: _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "grid h-9 w-9 shrink-0 place-items-center rounded-md bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30", children: _jsxs("svg", { viewBox: "0 0 24 24", className: "h-5 w-5", fill: "none", stroke: "currentColor", strokeWidth: "1.7", children: [_jsx("circle", { cx: "12", cy: "12", r: "9" }), _jsx("circle", { cx: "12", cy: "12", r: "5" }), _jsx("circle", { cx: "12", cy: "12", r: "1.6", fill: "currentColor", stroke: "none" })] }) }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsxs("div", { className: "flex items-baseline gap-2", children: [_jsx("span", { className: "font-mono text-base text-ink-50", children: currency || "—" }), _jsx("span", { className: "text-[10px] uppercase tracking-wide text-ink-500", children: "currency" })] }), _jsx("div", { className: `mt-0.5 truncate text-[11px] ${methodology ? "text-ink-300" : "text-ink-500"}`, title: methodology, children: methodology || "no methodology label" })] })] }) }), _jsx(PortGrid, { tone: tone, inputs: [{ name: "value" }], outputs: [] })] }));
}
export function SpreadsheetNode({ data, selected }) {
    const tone = TONES.spreadsheet;
    const inputs = data.inputs ?? [];
    const outputs = data.outputs ?? [];
    const cellCount = Object.keys(data.config?.cells ?? {}).length;
    return (_jsxs(Frame, { tone: tone, selected: !!selected, kind: "spreadsheet", children: [_jsx(Header, { tone: tone, title: data.label || "Spreadsheet" }), _jsxs(Body, { children: [_jsx(FieldLabel, { children: "Cells" }), _jsxs("div", { className: "font-mono text-sm text-ink-100", children: [cellCount, " defined"] }), data.errorCount && data.errorCount > 0 ? (_jsxs("div", { className: "mt-1 inline-block rounded border border-red-700/40 bg-red-900/20 px-2 py-0.5 text-[11px] text-red-200", children: [data.errorCount, " error", data.errorCount > 1 ? "s" : ""] })) : null] }), _jsx(PortGrid, { tone: tone, inputs: inputs.map((n) => ({
                    name: n,
                    kind: data.inputKinds?.[n] ?? "value",
                })), outputs: outputs.map((n) => ({
                    name: n,
                    kind: data.outputKinds?.[n] ?? "value",
                })) })] }));
}
export function TimelineSheetNode({ data, selected }) {
    const tone = TONES.timeline_sheet;
    const inputs = data.inputs ?? [];
    const outputs = data.outputs ?? [];
    const cfg = (data.config ?? {});
    const rowCount = Array.isArray(cfg.rows) ? cfg.rows.length : 0;
    const periodCount = Array.isArray(cfg.periods) ? cfg.periods.length : 0;
    const sched = cfg.period_schedule;
    const usingSchedule = !!sched && !!sched.anchor && !!sched.until;
    return (_jsxs(Frame, { tone: tone, selected: !!selected, kind: "timeline_sheet", children: [_jsx(Header, { tone: tone, title: data.label || "Timeline / Cash-flow" }), _jsxs(Body, { children: [_jsx(FieldLabel, { children: "Grid" }), _jsxs("div", { className: "font-mono text-sm text-ink-100", children: [rowCount, " row", rowCount === 1 ? "" : "s", " \u00B7", " ", usingSchedule
                                ? `schedule ${sched?.frequency ?? ""}`
                                : `${periodCount} period${periodCount === 1 ? "" : "s"}`] })] }), _jsx(PortGrid, { tone: tone, inputs: inputs.map((n) => ({
                    name: n,
                    kind: data.inputKinds?.[n] ?? "value",
                })), outputs: outputs.map((n) => ({
                    name: n,
                    kind: data.outputKinds?.[n] ?? "value",
                })) })] }));
}
export function DerivativesNode({ data, selected }) {
    const tone = TONES.derivatives;
    const inputs = data.inputs ?? [];
    const outputs = data.outputs ?? [];
    const cfg = (data.config ?? {});
    const legCount = Array.isArray(cfg.legs) ? cfg.legs.length : 0;
    const ir = cfg.ir_model?.kind ?? "deterministic";
    const credit = cfg.credit_model?.kind ?? "none";
    const engine = cfg.engine ?? "deterministic";
    const paramsFrom = cfg.ir_model?.params?.params_from;
    return (_jsxs(Frame, { tone: tone, selected: !!selected, kind: "derivatives", children: [_jsx(Header, { tone: tone, title: data.label || "Derivatives" }), _jsxs(Body, { children: [_jsx(FieldLabel, { children: "Position" }), _jsxs("div", { className: "font-mono text-sm text-ink-100", children: [legCount, " leg", legCount === 1 ? "" : "s", cfg.valuation_currency ? ` · ${cfg.valuation_currency}` : ""] }), _jsxs("div", { className: "mt-2 flex flex-wrap gap-1 text-[10px]", children: [_jsx("span", { className: "rounded border border-rose-500/30 bg-rose-500/10 px-1.5 py-0.5 text-rose-200", children: engine }), _jsxs("span", { className: "rounded border border-rose-500/30 bg-rose-500/10 px-1.5 py-0.5 text-rose-200", children: ["ir: ", ir] }), _jsxs("span", { className: "rounded border border-rose-500/30 bg-rose-500/10 px-1.5 py-0.5 text-rose-200", children: ["credit: ", credit] }), paramsFrom && (_jsxs("span", { className: "rounded border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 text-emerald-200", title: `IR params resolved from assumption "${paramsFrom}" at run time`, children: ["\u03C3 \u2190 ", paramsFrom] }))] })] }), _jsx(PortGrid, { tone: tone, inputs: inputs.map((id) => ({
                    name: id,
                    label: data.inputLabels?.[id] ?? id,
                    kind: data.inputKinds?.[id] ?? "value",
                })), outputs: outputs.map((id) => ({
                    name: id,
                    label: data.outputLabels?.[id] ?? id,
                    kind: data.outputKinds?.[id] ?? "value",
                })) })] }));
}
export function InputsAllNode({ data, selected }) {
    const tone = TONES["assumption.input"];
    return (_jsxs(Frame, { tone: tone, selected: !!selected, kind: "inputs.all", children: [_jsx(Header, { tone: tone, title: data.label || "All Inputs" }), _jsxs(Body, { children: [_jsx(FieldLabel, { children: "Bus producer" }), _jsx("div", { className: "text-sm text-ink-100", children: "assumptions \u00B7 parameters \u00B7 context" }), _jsxs("p", { className: "mt-1 text-[11px] text-ink-400", children: ["Connect to a Spreadsheet's bus input to expose every key on this approach to ", _jsx("span", { className: "font-mono", children: "INPUT()" }), "."] })] }), _jsx(PortGrid, { tone: tone, inputs: [], outputs: [{ name: "all", kind: "bus" }] })] }));
}
// --- Building blocks ---------------------------------------------------------
function Frame({ tone, selected, kind, children, }) {
    return (_jsx("div", { "data-kind": kind, className: `min-w-[220px] overflow-hidden rounded-lg border ${tone.ringColor} ${tone.surface} shadow-md ${selected ? tone.selectedRing : ""}`, children: children }));
}
function Header({ tone, title }) {
    return (_jsxs("div", { className: `flex items-center justify-between border-b ${tone.divider} ${tone.headerBg} px-4 py-1.5`, children: [_jsx("span", { className: `rounded px-1.5 py-0.5 text-[10px] uppercase tracking-[0.12em] ${tone.chipClass}`, children: tone.chipLabel }), _jsx("span", { className: "truncate text-sm font-medium text-ink-50", children: title })] }));
}
function Body({ children }) {
    return _jsx("div", { className: "px-4 py-3", children: children });
}
function FieldLabel({ children }) {
    return (_jsx("div", { className: "text-[10px] uppercase tracking-wide text-ink-400", children: children }));
}
/**
 * Renders the input / output ports as a two-column grid below the node body.
 * Each port row CONTAINS its own Handle so the dot is always aligned with
 * the label on the same row, regardless of how tall the body is.
 */
function PortGrid({ tone, inputs, outputs, }) {
    if (inputs.length === 0 && outputs.length === 0)
        return null;
    return (_jsxs("div", { className: `grid grid-cols-2 border-t ${tone.divider}`, children: [_jsx("div", { className: `border-r ${tone.divider}`, children: inputs.length === 0 ? (_jsx(Empty, { side: "left", children: "no inputs" })) : (inputs.map((p) => (_jsx(PortRow, { name: p.name, label: p.label, kind: p.kind ?? "value", side: "left" }, `in-${p.name}`)))) }), _jsx("div", { children: outputs.length === 0 ? (_jsx(Empty, { side: "right", children: "no outputs" })) : (outputs.map((p) => (_jsx(PortRow, { name: p.name, label: p.label, kind: p.kind ?? "value", side: "right" }, `out-${p.name}`)))) })] }));
}
function Empty({ side, children }) {
    return (_jsx("div", { className: `flex h-8 items-center px-3 text-[10px] text-ink-500 ${side === "right" ? "justify-end" : ""}`, children: children }));
}
function PortRow({ name, label, kind, side, }) {
    // Each row is `relative` so the absolutely-positioned Handle anchors here.
    const display = label && label.length > 0 ? label : name;
    const isBus = kind === "bus";
    const tooltip = (label && label !== name ? `${label} (id: ${name})` : undefined) ||
        (isBus ? "bus port — multiplexed key→value connection" : undefined);
    return (_jsxs("div", { className: `relative flex h-8 items-center gap-2 px-3 text-xs font-mono ${isBus ? "text-fuchsia-200" : "text-ink-200"} ${side === "right" ? "justify-end" : ""}`, title: tooltip, children: [_jsx(Handle, { id: name, type: side === "left" ? "target" : "source", position: side === "left" ? Position.Left : Position.Right, isConnectable: true, className: isBus ? HANDLE_BUS : HANDLE_DOT }), _jsxs("span", { className: !label ? "text-ink-500 italic" : "", children: [display, isBus && (_jsx("span", { className: "ml-1 align-middle text-[9px] uppercase tracking-wide text-fuchsia-300", children: "bus" }))] })] }));
}
export function AIAgentNode({ data, selected }) {
    const tone = TONES["ai.agent"];
    const inputs = data.inputs ?? [];
    const outputs = data.outputs ?? [];
    const agentName = data.config?.agent_name || "(no agent picked)";
    const instructions = data.config?.instructions || "";
    return (_jsxs(Frame, { tone: tone, selected: !!selected, kind: "ai.agent", children: [_jsx(Header, { tone: tone, title: data.label || "AI Agent" }), _jsxs(Body, { children: [_jsx(FieldLabel, { children: "Agent" }), _jsx("div", { className: "text-sm text-ink-100", children: agentName }), instructions && (_jsxs(_Fragment, { children: [_jsx("div", { className: "mt-2 text-[10px] uppercase tracking-wide text-ink-400", children: "Instructions" }), _jsx("div", { className: "mt-0.5 text-[11px] text-ink-300 line-clamp-2", title: instructions, children: instructions })] }))] }), _jsx(PortGrid, { tone: tone, inputs: inputs.map((id) => ({
                    name: id,
                    label: data.inputLabels?.[id] ?? id,
                    kind: data.inputKinds?.[id] ?? "value",
                })), outputs: outputs.map((id) => ({
                    name: id,
                    label: data.outputLabels?.[id] ?? id,
                    kind: data.outputKinds?.[id] ?? "value",
                })) })] }));
}
export function ScenarioChooserNode({ data, selected }) {
    const tone = TONES.scenario_chooser;
    const inputs = data.inputs ?? [];
    const outputs = data.outputs ?? ["value"];
    return (_jsxs(Frame, { tone: tone, selected: !!selected, kind: "scenario_chooser", children: [_jsx(Header, { tone: tone, title: data.label || "Scenario chooser" }), _jsxs(Body, { children: [_jsx(FieldLabel, { children: "Multiplexer" }), _jsxs("p", { className: "text-[11px] text-ink-400", children: ["Routes the input matching the active scenario name to", " ", _jsx("span", { className: "font-mono", children: "value" }), ". A port named", " ", _jsx("span", { className: "font-mono", children: "Base" }), " is the default fallback."] }), inputs.length > 0 && (_jsx("div", { className: "mt-2 flex flex-wrap gap-1 text-[10px]", children: inputs.map((id) => (_jsx("span", { className: "rounded bg-violet-500/15 px-1.5 py-0.5 text-violet-200", children: data.inputLabels?.[id] ?? id }, id))) }))] }), _jsx(PortGrid, { tone: tone, inputs: inputs.map((id) => ({
                    name: id,
                    label: data.inputLabels?.[id] ?? id,
                    kind: data.inputKinds?.[id] ?? "value",
                })), outputs: outputs.map((id) => ({
                    name: id,
                    label: data.outputLabels?.[id] ?? id,
                    kind: data.outputKinds?.[id] ?? "value",
                })) })] }));
}
export function ExpressionNode({ data, selected }) {
    const tone = TONES.expression;
    const inputs = data.inputs ?? [];
    const outputs = data.outputs ?? ["value"];
    const expr = data.config?.expression ?? "";
    return (_jsxs(Frame, { tone: tone, selected: !!selected, kind: "expression", children: [_jsx(Header, { tone: tone, title: data.label || "Expression" }), _jsxs(Body, { children: [_jsx(FieldLabel, { children: "Formula" }), _jsx("pre", { className: "mt-0.5 max-h-16 overflow-auto rounded bg-ink-900/40 px-2 py-1 font-mono text-[11px] text-sky-200", children: expr ? (expr.startsWith("=") ? expr : "= " + expr) : "(empty — set in config panel)" })] }), _jsx(PortGrid, { tone: tone, inputs: inputs.map((id) => ({
                    name: id,
                    label: data.inputLabels?.[id] ?? id,
                    kind: data.inputKinds?.[id] ?? "value",
                })), outputs: outputs.map((id) => ({
                    name: id,
                    label: data.outputLabels?.[id] ?? id,
                    kind: data.outputKinds?.[id] ?? "value",
                })) })] }));
}
export function ConfidenceGateNode({ data, selected }) {
    const tone = TONES.confidence_gate;
    const min = data.config?.min_confidence ?? 3;
    const requireSet = data.config?.require_confidence_set ?? true;
    return (_jsxs(Frame, { tone: tone, selected: !!selected, kind: "confidence_gate", children: [_jsx(Header, { tone: tone, title: data.label || "Confidence gate" }), _jsxs(Body, { children: [_jsx(FieldLabel, { children: "Minimum confidence" }), _jsxs("div", { className: "mt-0.5 flex items-baseline gap-2", children: [_jsxs("span", { className: "font-mono text-base text-yellow-200", children: ["\u2265 C", min] }), _jsx("span", { className: "text-[11px] text-ink-400", children: min === 4 ? "user-provided"
                                    : min === 3 ? "AI high"
                                        : min === 2 ? "AI medium"
                                            : "AI low" })] }), !requireSet && (_jsx("p", { className: "mt-1 text-[10px] text-amber-300", children: "\u26A0 allows values with no confidence metadata to pass" }))] }), _jsx(PortGrid, { tone: tone, inputs: [{ name: "value", label: "value", kind: "value" }], outputs: [{ name: "value", label: "value", kind: "value" }] })] }));
}
export const NODE_TYPES = {
    "assumption.input": AssumptionInputNode,
    spreadsheet: SpreadsheetNode,
    output: OutputNode,
    "ai.agent": AIAgentNode,
    "inputs.all": InputsAllNode,
    timeline_sheet: TimelineSheetNode,
    derivatives: DerivativesNode,
    scenario_chooser: ScenarioChooserNode,
    expression: ExpressionNode,
    confidence_gate: ConfidenceGateNode,
};
